import { ApolloError } from '@apollo/client';
import { recordMark } from '@stitch-fix/client-analytics-reporter';
import { type RiskyLoginConfirmMutation, useRiskyLoginConfirmMutation } from '../../pages/confirm-login/RiskyLoginConfirmMutation';
import type { RiskyLoginConfirmError, RiskyLoginConfirmInput } from '../../types/definitions/vendor/graphql-schema';
import { browserLogger } from '../../utils/loggers/browser';
const logError = ({
  message,
  error,
  loginEventId
}: RiskyLoginConfirmInput & {
  message: string;
  error?: ApolloError | RiskyLoginConfirmError;
}) => {
  browserLogger({
    level: 'error',
    team: 'client-onboarding',
    message,
    context: {
      loginEventId,
      error
    }
  });
  recordMark('risky_login_confirm_mutation_completed', {
    graph_mutation_succeeded: false
  });
};

/**
 * This hook is a wrapper around the useRiskyLoginConfirmMutation and logging behavior
 * that allows it to be used in multiple places easily with consistent logging.
 *
 * @param input input that will be passed into the mutation
 *
 * @example
 * const [confirmRiskyLogin] = useRiskyLogin({
 *   loginEventId: '9999-9999-9999-9999-9999',
 *   status: 'safe',
 * });
 * ...
 * confirmRiskyLogin();
 */
export const useRiskyLogin = (input: RiskyLoginConfirmInput) => {
  return useRiskyLoginConfirmMutation({
    variables: {
      input
    },
    onError: error => {
      logError({
        message: 'RiskyLoginConfirmMutation error',
        error,
        ...input
      });
    },
    onCompleted: ({
      riskyLoginConfirm
    }: RiskyLoginConfirmMutation) => {
      if (!riskyLoginConfirm?.result) {
        logError({
          message: 'RiskyLoginConfirmMutation did not return a confirmation',
          ...input
        });
        return;
      }
      const {
        result
      } = riskyLoginConfirm;
      if ('riskyLoginConfirmation' in result) {
        recordMark('risky_login_confirm_mutation_completed', {
          graph_mutation_succeeded: true
        });
      } else {
        logError({
          message: 'RiskyLoginConfirmMutation failed to confirm the login',
          error: {
            code: result.code,
            message: result.message
          },
          ...input
        });
      }
    }
  });
};